import React, { useRef } from 'react';
import { useDispatch } from 'react-redux';
import { LazyRender } from '@noths/polaris-client-ribbons-design-system';
import dynamic from 'next/dynamic';

import { BasketErrorNotification } from 'src/components/atoms/BasketErrorNotification/BasketErrorNotification';
import { PARTNER_MODULE_ID } from 'src/components/layouts/PDP/elementIds';
import { BasketCTAButtonContainer } from 'src/components/molecules/BasketCTAButton/BasketCTAButtonContainer';
import { MAIN_PAGE_CTA_ID } from 'src/components/molecules/BasketCTAButton/elementIDs';
import { trackOpenContactPartnerModal } from 'src/components/molecules/ContactPartnerModal/tracking/contactPartnerModalEvents';
import { DeliverySummary } from 'src/components/molecules/DeliverySummary/DeliverySummary';
import { useDeliverySummary } from 'src/components/molecules/DeliverySummary/useDeliverySummary';
import { ExpandablePanel } from 'src/components/molecules/ExpandablePanel/ExpandablePanel';
import { MembershipBannerSection } from 'src/components/molecules/MembershipBannerSection/MembershipBannerSection';
import { PartnerMaxiModuleWithTracking } from 'src/components/molecules/PartnerMaxiModule/components/PartnerMaxiModuleWithTracking';
import { ProductRatingMaxiSummary } from 'src/components/molecules/ProductRatingMaxiSummary/ProductRatingMaxiSummary';
import { PublicAttributes } from 'src/components/molecules/PublicAttributes/PublicAttributes';
import { StickySection } from 'src/components/molecules/StickySection/components/StickySection';
import { TrustCarouselWithTracking } from 'src/components/molecules/TrustCarousel/components/TrustCarouselWithTracking';
import { LinkCarouselContainer } from 'src/components/organisms/LinkCarousel/containers/LinkCarouselContainer';
import { ProductImagesCarousel } from 'src/components/organisms/ProductImagesCarousel/ProductImagesCarousel';
import { useProductImagesCarouselActions } from 'src/components/organisms/ProductImagesCarousel/useProductImagesCarouselActions';
import { ProductImagesModalContainer } from 'src/components/organisms/ProductImagesModal/ProductImagesModalContainer';
import { ProductInformation } from 'src/components/organisms/ProductInformation/components/ProductInformation';
import { ProductOptionsContainer } from 'src/components/organisms/ProductOptions/ProductOptionsContainer';
import { ProductReviewsContainer } from 'src/components/organisms/ProductReviews/containers/ProductReviewsContainer';
import { RecommendedProductsCarouselContainer } from 'src/components/organisms/RecommendedProductsCarousel/containers/RecommendedProductsCarouselContainer';
import { REVIEWS_URL_FRAGMENT_ID } from 'src/fragmentIDs';
import { actions } from 'src/redux/contactPartner/slice';
import { requestPartnerContent, requestPartnerInsights } from 'src/redux/partner/thunks';
import type { AppThunkDispatch } from 'src/redux/store';
import { trackBreadcrumbClick } from 'src/tracking/trackBreadcrumbClick';
import { trackProductInformationToggle } from 'src/tracking/trackProductInformationToggle';
import { trackProductInformationVisible } from 'src/tracking/trackProductInformationVisible';
import { trackPublicAttributesToggle } from 'src/tracking/trackPublicAttributesToggle';
import { useEditProductOptions } from '../hooks/useEditProductOptions';
import { PDPLayoutView } from './PDPLayoutView';
import { usePDPViewModel } from './usePDPViewModel';
import * as styles from './PDPLayoutView.styles';

const DeliveryDrawer = dynamic(() =>
  import(
    /* webpackChunkName: "delivery_drawer" */ 'src/components/organisms/DeliveryDrawer/DeliveryDrawer'
  ).then((mod) => mod.DeliveryDrawer),
);

const ProductOptionsDrawerContainer = dynamic(() =>
  import(
    /* webpackChunkName: "product_options_drawer" */ 'src/components/organisms/ProductOptions/ProductOptionsDrawerContainer'
  ).then((mod) => mod.ProductOptionsDrawerContainer),
);

const RECOMMENDED_PRODUCTS_LAZY_LOAD_MARGIN = '200px';

export const PDPLayout = () => {
  const dispatch = useDispatch();
  const {
    deliveryOptionsLoaded,
    hasProductOptions,
    partnerMaxi,
    similarProductsServingConfig,
    viewModel,
  } = usePDPViewModel();
  const deliverySummaryProps = useDeliverySummary();
  const imagesCarouselActions = useProductImagesCarouselActions();
  const rightColumnRef = useRef<HTMLDivElement>(null);

  useEditProductOptions();

  const openContactPartnerModal = () => {
    dispatch(actions.openContactPartnerModal());
    trackOpenContactPartnerModal(viewModel.partner.name);
  };

  return (
    <>
      <PDPLayoutView
        ctaColumnRef={rightColumnRef}
        ctaTrigger={
          <>
            {deliveryOptionsLoaded && <DeliverySummary {...deliverySummaryProps} />}
            <ProductOptionsContainer />
            <BasketCTAButtonContainer />
          </>
        }
        images={<ProductImagesCarousel {...viewModel.images} {...imagesCarouselActions} />}
        linkCarousel={<LinkCarouselContainer />}
        membershipBanner={<MembershipBannerSection />}
        onBreadcrumbLinkClick={trackBreadcrumbClick}
        onPartnerMiniModuleVisible={() => {
          (dispatch as AppThunkDispatch)(
            requestPartnerContent({ shortcode: partnerMaxi.shortcode }),
          );
          (dispatch as AppThunkDispatch)(
            requestPartnerInsights({ shortcode: partnerMaxi.shortcode }),
          );
        }}
        partnerModules={
          <div id={PARTNER_MODULE_ID}>
            <PartnerMaxiModuleWithTracking
              location={viewModel.partner.location}
              logoSrc={viewModel.partner.logoSrc}
              name={viewModel.partner.name}
              onContactPartnerClick={openContactPartnerModal}
              partnerLinkHref={partnerMaxi.linkHref}
              partnerRating={viewModel.partnerReviews.rating}
              partnerReviewCount={viewModel.partnerReviews.count}
              partnerShortcode={partnerMaxi.shortcode}
            />
            <div css={styles.partnerProductsCarousel}>
              <LazyRender options={{ rootMargin: RECOMMENDED_PRODUCTS_LAZY_LOAD_MARGIN }}>
                <RecommendedProductsCarouselContainer
                  servingConfig="pdp_same_seller_2"
                  title="Our bestsellers"
                />
              </LazyRender>
            </div>
          </div>
        }
        productInformation={
          <ExpandablePanel
            collapseButtonText="Read less"
            expandButtonText="Read more"
            onToggle={trackProductInformationToggle}
          >
            <ProductInformation
              {...viewModel.productInformation}
              onVisible={trackProductInformationVisible}
            />
          </ExpandablePanel>
        }
        publicAttributes={
          <ExpandablePanel
            collapseButtonText="Read less"
            expandButtonText="Read more"
            onToggle={trackPublicAttributesToggle}
          >
            <PublicAttributes publicAttributes={viewModel.publicAttributes} />
          </ExpandablePanel>
        }
        recommendedProducts={
          <LazyRender options={{ rootMargin: RECOMMENDED_PRODUCTS_LAZY_LOAD_MARGIN }}>
            <RecommendedProductsCarouselContainer
              servingConfig={similarProductsServingConfig}
              title="Similar products"
            />
          </LazyRender>
        }
        reviews={
          <div id={REVIEWS_URL_FRAGMENT_ID}>
            <div css={styles.overallRatingWrapper}>
              <ProductRatingMaxiSummary
                averageRating={viewModel.productReviews.rating}
                numberOfReviews={viewModel.productReviews.count}
              />
            </div>
            <div css={styles.productReviewsWrapper}>
              <ProductReviewsContainer />
            </div>
          </div>
        }
        stickyCta={
          <StickySection triggerElementId={MAIN_PAGE_CTA_ID}>
            <BasketCTAButtonContainer />
          </StickySection>
        }
        trustCarousel={<TrustCarouselWithTracking />}
        viewModel={viewModel}
      />
      <ProductImagesModalContainer />
      <DeliveryDrawer target={rightColumnRef} />
      {hasProductOptions && <ProductOptionsDrawerContainer target={rightColumnRef} />}
      <BasketErrorNotification />
    </>
  );
};
